import type { RequestedSupportingItemProps } from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import {
  type Checklist as ChecklistProto,
  type RequestedSupportingItem as RequestedSupportingItemProto,
  type Section as SectionProto,
  SupportingItemInput as SupportingItemInputProto,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import type { SupportingItemInputProps } from '@/features/jobs/details/tax-type/info-gathering/supporting-item-input'
import { Id } from '@/infra/id'
import type {
  ChecklistProps,
  ChecklistSectionProps,
} from '@/features/jobs/details/tax-type/info-gathering/Checklist'

export function mapChecklistProtoToProps(
  jobId: string,
  proto: ChecklistProto,
): ChecklistProps {
  return {
    jobId,
    sections: proto.getSectionsList().map(mapSectionProtoToProps),
  }
}

export function mapSectionProtoToProps(
  proto: SectionProto,
): ChecklistSectionProps {
  return {
    id: proto.getId(),
    name: proto.getName(),
    heading: proto.getHeading(),
    description: proto.getDescription(),
    requestedSupportingItems: proto
      .getRequestedSupportingItemsList()
      .map(mapRequestedSupportingItemProtoToProps),
  }
}

export function mapRequestedSupportingItemProtoToProps(
  proto: RequestedSupportingItemProto,
): RequestedSupportingItemProps {
  const inputProto = proto.getInput()
  return {
    id: proto.getId(),
    adHoc: proto.getAdHoc(),
    heading: proto.getHeading(),
    shortDescription: proto.getShortDescription(),
    longDescription: proto.getLongDescription(),
    name: proto.getName(),
    imageUrl: proto.getImageUrl()?.getValue() ?? null,
    previewUrl: proto.getPreviewUrl()?.getValue() ?? null,
    required: proto.getRequired(),
    input: inputProto
      ? mapSupportingItemInputProtoToProps(inputProto)
      : {
          type: 'Notes',
          text: '',
        },
  }
}

function mapSupportingItemInputProtoToProps(
  proto: SupportingItemInputProto,
): SupportingItemInputProps {
  switch (proto.getTypeCase()) {
    case SupportingItemInputProto.TypeCase.PERSONAL_INFO: {
      const personalInfoProto = proto.getPersonalInfo()!
      const profileId = personalInfoProto.getProfileId()?.getValue()
      return {
        type: 'PersonalInfo',
        provided: profileId
          ? {
              profileId: profileId,
              profileVersion:
                personalInfoProto.getProfileVersion()?.getValue() ?? 0,
            }
          : null,
      }
    }
    case SupportingItemInputProto.TypeCase.SPOUSAL_INFO: {
      const spousalInfoProto = proto.getSpousalInfo()!
      const profileId = spousalInfoProto.getProfileId()?.getValue()
      return {
        type: 'SpousalInfo',
        provided: profileId
          ? {
              profileId: profileId,
              profileVersion:
                spousalInfoProto.getProfileVersion()?.getValue() ?? 0,
            }
          : null,
      }
    }
    case SupportingItemInputProto.TypeCase.DEPENDENTS_INFO: {
      const dependentsInfoProto = proto.getDependentsInfo()!
      return {
        type: 'DependentsInfo',
        dependents: dependentsInfoProto
          .getDependentsList()
          .map((dependent) => ({
            profileId: Id(dependent.getProfileId()?.getValue()),
            profileVersion: dependent.getProfileVersion()?.getValue() ?? 0,
          })),
      }
    }
    case SupportingItemInputProto.TypeCase.PERSONAL_REFUND_METHOD: {
      const methodProto = proto.getPersonalRefundMethod()!
      const methodId = methodProto.getRefundMethodId()?.getValue()
      return {
        type: 'PersonalTaxRefundMethod',
        provided: methodId
          ? {
              refundMethodId: methodId,
              refundMethodVersion:
                methodProto.getRefundMethodVersion()?.getValue() ?? 0,
            }
          : null,
      }
    }
    case SupportingItemInputProto.TypeCase.BUSINESS_INFO: {
      const businessInfoProto = proto.getBusinessInfo()!
      const profileId = businessInfoProto.getProfileId()?.getValue()
      return {
        type: 'BusinessInfo',
        provided: profileId
          ? {
              profileId: profileId,
              profileVersion:
                businessInfoProto.getProfileVersion()?.getValue() ?? 0,
            }
          : null,
      }
    }
    case SupportingItemInputProto.TypeCase.BUSINESS_REFUND_METHOD: {
      const methodProto = proto.getBusinessRefundMethod()!
      const methodId = methodProto.getRefundMethodId()?.getValue()
      return {
        type: 'BusinessTaxRefundMethod',
        provided: methodId
          ? {
              refundMethodId: methodId,
              refundMethodVersion:
                methodProto.getRefundMethodVersion()?.getValue() ?? 0,
            }
          : null,
      }
    }
    case SupportingItemInputProto.TypeCase.DOCUMENT: {
      const docProto = proto.getDocument()!
      return {
        type: 'Document',
        documentTypeShortId: docProto.getDocumentTypeShortId(),
        documentLongIds: docProto.getDocumentLongIdsList(),
      }
    }
    // Fall back to notes since it's the simplest one.
    case SupportingItemInputProto.TypeCase.NOTES:
    case SupportingItemInputProto.TypeCase.TYPE_NOT_SET:
    default: {
      return {
        type: 'Notes',
        text: proto.getNotes()?.getText()?.getValue() ?? '',
      }
    }
  }
}
